import React from 'react';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { HEADER_THEME_CSS } from '@src/common/Table';
import {
  buildCsvFromRows,
  downloadCsv,
  mapV1ColumnsToV2,
  withRowIds,
} from '@src/utils/tableUtils';

// Shared navy section-bar style matching the legacy WLM gateway UI.
export const sectionBarStyle: React.CSSProperties = {
  backgroundColor:
    'var(--abyss-colors-web-semantic-color-surface-container-primary)',
  color: 'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
  textAlign: 'center',
  fontWeight: 'bold',
  padding: '8px',
};

// Backend sends multi-line text as '\n' or '<br>'-delimited strings (legacy
// JSP injected it via innerHTML); split safely instead of rendering raw HTML.
const LINE_BREAK_PATTERN = /\r\n|\r|\n|<br\s*\/?>/gi;
export const renderMultilineText = (text?: string | null) =>
  text
    ?.split(LINE_BREAK_PATTERN)
    .map((line, index) => <div key={index}>{line}</div>);

// Renders one named bucket of WLM summary rows; always shown (even empty)
// until the backend endpoints are wired up to real data.
// `downloadFilename`, when provided, adds a Download CSV dropdown (exports
// the currently loaded rows, matching the common Table's download pattern).
export const SummaryTable = ({
  title,
  columns,
  data,
  downloadFilename,
}: any) => {
  const v2Columns = React.useMemo(() => mapV1ColumnsToV2(columns), [columns]);
  const rows = React.useMemo(() => withRowIds(data || []), [data]);

  const dataTableProps = useDataTable<any>({
    initialData: rows,
    initialColumns: v2Columns,
    rowIdKey: 'uniqueId',
    paginationConfig: { enablePagination: false },
  });

  React.useEffect(() => {
    dataTableProps.setData(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  React.useEffect(() => {
    dataTableProps.setColumns(v2Columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2Columns]);

  return (
    <div style={{ paddingBottom: '15px' }}>
      <div style={sectionBarStyle}>{title}</div>
      <DataTable hideHeader title="" tableState={dataTableProps}>
        {downloadFilename && (
          <Flex justify="flex-end" css={{ padding: '8px 16px' }}>
            <DataTable.DownloadDropdown
              dropdownMenuItems={[
                {
                  title: 'Download CSV',
                  onClick: () =>
                    downloadCsv(
                      buildCsvFromRows(rows, columns),
                      downloadFilename
                    ),
                },
              ]}
            />
          </Flex>
        )}
        <DataTable.Table
          css={{
            width: '100%',
            ...HEADER_THEME_CSS,
            // Match common/Table's flex override so header/cell widths stay in
            // sync with the column virtualizer's size calculations.
            'abyss-data-table-base-cell': {
              flex: '1 0 auto',
              color: '#212223',
              fontWeight: 500,
            },
            'abyss-data-table-header-container': {
              ...HEADER_THEME_CSS['abyss-data-table-header-container'],
              flex: '1 0 auto',
            },
          }}
        />
      </DataTable>
    </div>
  );
};
